import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Search } from 'lucide-react';
import { supabase, isUserAdmin } from '../../../lib/supabase';

interface Mentee {
  uuid: string;
  name: string;
  email: string;
  date_joined: string;
}

interface MenteeData {
  uuid: string;
  user_uuid: string;
  vincer_base_user: {
    name: string;
    email: string;
    date_joined: string;
  };
}

export function MenteeList() {
  const navigate = useNavigate();
  const [mentees, setMentees] = useState<Mentee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    async function checkAdminStatus() {
      const adminStatus = await isUserAdmin();
      setIsAdmin(adminStatus);
    }
    checkAdminStatus();
  }, []);

  useEffect(() => {
    async function fetchMentees() {
      try {
        console.log('Fetching mentees...');
        const { data: menteeData, error: menteeError } = await supabase
          .from('mentee')
          .select(`
            uuid,
            user_uuid,
            vincer_base_user (
              name,
              email,
              date_joined
            )
          `);

        if (menteeError) {
          console.error('Supabase error:', menteeError);
          throw menteeError;
        }

        if (!menteeData) {
          console.error('No data returned from Supabase');
          throw new Error('No data returned from database');
        }

        console.log('Raw mentee data:', menteeData);

        const formattedMentees = (menteeData as any[]).map(mentee => {
          const baseUser = Array.isArray(mentee.vincer_base_user) 
            ? mentee.vincer_base_user[0] 
            : mentee.vincer_base_user;

          return {
            uuid: mentee.uuid,
            name: baseUser.name,
            email: baseUser.email,
            date_joined: baseUser.date_joined
          };
        });

        console.log('Formatted mentees:', formattedMentees);
        setMentees(formattedMentees);
      } catch (err) {
        console.error('Error fetching mentees:', err);
        setError(err instanceof Error ? err.message : 'Failed to load mentees');
      } finally {
        setLoading(false);
      }
    }

    fetchMentees();
  }, []);

  const filteredMentees = mentees.filter(mentee =>
    mentee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    mentee.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Mentees</h1>
        {isAdmin && (
          <button
            onClick={() => navigate('/coordinator/mentees/add')}
            className="flex items-center gap-2 bg-[#4CAF50] text-white px-4 py-2 rounded-lg hover:bg-[#45a049] transition-colors"
          >
            <UserPlus className="w-5 h-5" />
            <span>Add Mentee</span>
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search mentees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#4CAF50] focus:outline-none focus:ring-[#4CAF50]"
            />
          </div>
        </div>

        {error ? (
          <div className="p-4 text-red-600">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-6 py-3 text-gray-600 text-sm font-medium">Name</th>
                  <th className="px-6 py-3 text-gray-600 text-sm font-medium">Email</th>
                  <th className="px-6 py-3 text-gray-600 text-sm font-medium">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredMentees.map((mentee) => (
                  <tr key={mentee.uuid} className="hover:bg-gray-50">
                    <td className="px-6 py-4">{mentee.name}</td>
                    <td className="px-6 py-4">{mentee.email}</td>
                    <td className="px-6 py-4">
                      {new Date(mentee.date_joined).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
                {filteredMentees.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                      No mentees found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
} 